import { createAction } from 'redux-actions';
import {
  SET_USERS,
  GET_USER_BY_PNR,
  GET_DONOR_DETAILS,
  GET_DONOR_SETTINGS,
  REQUEST_FAIL,
  REQUEST_PENDING,
  DELETE_DONOR_DETAILS,
  DELETE_DONOR_SETTINGS,
  TOGGLE_DONOR_DETAILS_MODAL,
  TOGGLE_DONOR_SETTINGS_MODAL,
  SET_LAST_CHOOSEN_DONOR,
  SET_SEARCH_PNR,
  SET_USERS_PAGE,
  SET_USERS_AMOUNT,
  SET_DONATION_SITES,
} from './actionTypes';
import { DONOR_LIST_PAGINATION } from '../../constants/pagination';
import { DONORS_SORT_KEYS, DONORS_DEFAULT_SORT } from '../../constants/donors';
import {
  getFromUsers,
  getUnEncryptedUserByPnr,
  getUnEncryptedUserById,
  getUser,
  getDonationSites as fetchDonationSites,
} from '../../services/fetch';
import { getValidIdToken, getIdToken } from "../auth/actions";
import { sendErrorLog } from "../logging/actions";
import { addPendingRequest, deletePendingRequestIfCurrent } from '../requests/actions';
import { getAbortController } from '../../services/helpers';

export const setUsers = createAction(SET_USERS);
export const userByPnr = createAction(GET_USER_BY_PNR);
export const getDonorDetails = createAction(GET_DONOR_DETAILS);
export const getDonorSettings = createAction(GET_DONOR_SETTINGS);
export const requestFail = createAction(REQUEST_FAIL);
export const requestPending = createAction(REQUEST_PENDING);
export const deleteDonorDetails = createAction(DELETE_DONOR_DETAILS);
export const deleteDonorSettings = createAction(DELETE_DONOR_SETTINGS);
export const toggleDonorDetailsModal = createAction(TOGGLE_DONOR_DETAILS_MODAL);
export const toggleDonorSettingsModal = createAction(TOGGLE_DONOR_SETTINGS_MODAL);
export const setLastChoosenDonor = createAction(SET_LAST_CHOOSEN_DONOR);
export const setSearchPnr = createAction(SET_SEARCH_PNR);
// payload: { content: [...projected rows], paging: { page, page_size, total,
//            total_pages, sort, order, truncated } (server),
//            requested: { page, pageSize, sort, order }, bloodbankId, scopeKey }
export const setUsersPage = createAction(SET_USERS_PAGE);
// payload: { total, bloodbankId } — the candidate count of the message editor
export const setUsersAmount = createAction(SET_USERS_AMOUNT);
// payload: GET /v1/bloodbanks `content` — the donation-site directory
export const setDonationSites = createAction(SET_DONATION_SITES);

// CMT-57: the donation-site directory behind the «Valgt sted» dropdown in
// «Giverdetaljer» (every bank with `active` + its `reference` path, so the
// same-HF siblings of the donor's site can be offered — also banks the staff
// member has no grant for). Fetched each time the modal opens for an admin;
// a failure is logged and keeps whatever list the store already holds
// (nothing on the first open → the field stays read-only).
export const getDonationSites = () => (dispatch) =>
  fetchDonationSites()
    .then((res) => dispatch(setDonationSites(Array.isArray(res && res.content) ? res.content : [])))
    .catch((err) => {
      dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    });

// The donor list is paged AND sorted SERVER-SIDE:
//   POST /v1/donors/batch-get  body = audience filters (toDonorFilterPayload)
//     + page (1-based), page_size (20|50|100), sort (DONORS_SORT_KEYS),
//       order (asc|desc)
//   -> { content: [projected rows, never the encrypted blob],
//        paging: { page, page_size, total, total_pages, sort, order, truncated } }
// Without the four paging keys the API answers in legacy mode: every
// matching row plus a `paging` block with the total.
export const DONORS_PAGE_SIZES = DONOR_LIST_PAGINATION.pageSizes;
export const DONORS_DEFAULT_PAGE_SIZE = DONOR_LIST_PAGINATION.pageSize;

const normalizePage = (page) => (Number.isInteger(Number(page)) && Number(page) >= 1 ? Number(page) : 1);
const normalizePageSize = (pageSize) =>
  (DONORS_PAGE_SIZES.includes(Number(pageSize)) ? Number(pageSize) : DONORS_DEFAULT_PAGE_SIZE);
export const normalizeDonorsSort = (sort, order) => ({
  key: DONORS_SORT_KEYS.includes(sort) ? sort : DONORS_DEFAULT_SORT.key,
  order: order === 'asc' || order === 'desc' ? order : DONORS_DEFAULT_SORT.order,
});

// { page, pageSize, sort, order } the API accepts, from whatever the caller passed
export const normalizeDonorsPaging = ({ page, pageSize, sort, order } = {}) => {
  const sorting = normalizeDonorsSort(sort, order);
  return { page: normalizePage(page), pageSize: normalizePageSize(pageSize), sort: sorting.key, order: sorting.order };
};

export const donorsBatchGetBody = (payload, paging) => {
  if (!paging) return payload;
  const { page, pageSize, sort, order } = normalizeDonorsPaging(paging);
  return { ...payload, page, page_size: pageSize, sort, order };
};

// Identity of a (bloodbank, filters) combination: a page and a total belong
// to exactly one of these — a page that lands after the filters changed is
// not shown for the new ones.
export const donorsScopeKey = (payload) => {
  const { bloodbank, ...filters } = payload || {};
  const keys = Object.keys(filters).sort();
  return `${bloodbank || ''}|${JSON.stringify(keys.map((k) => [k, filters[k]]))}`;
};

// In-flight guard (same pattern as reservations getDailyReservations): a new
// call aborts a same-key predecessor, so listener-driven refetch bursts and
// a page flip can never stack concurrent batch-get requests — only the
// newest one lands. Always resolves (an abort or a failure is swallowed
// after dispatching requestFail) so callers can clear a spinner in
// `.finally`.
const guardedBatchGet = (functionKey, body, onSuccess) => (dispatch) => {
  const { signal, abortController } = getAbortController();
  dispatch(addPendingRequest({ id: functionKey, controller: abortController }));

  return getFromUsers('/v1/donors/batch-get', body, { signal })
  .then((res) => dispatch(onSuccess(res)))
  .catch((err) => {
    // superseded by a newer same-key call — not an error surface
    if (err.name === 'AbortError') return;
    return dispatch(requestFail(err));
  })
  .finally(() => dispatch(deletePendingRequestIfCurrent({ id: functionKey, controller: abortController })));
};

// One server page of the donor list. `paging` omitted = the legacy call
// (every row, `setUsers`) — kept for callers that need the whole set.
export const getFilteredUsers = (payload, paging) => {
  if (!paging) return guardedBatchGet('getFilteredUsers', payload, setUsers);
  const requested = normalizeDonorsPaging(paging);
  return guardedBatchGet('getFilteredUsers', donorsBatchGetBody(payload, requested), (res) => setUsersPage({
    content: res && res.content,
    paging: res && res.paging,
    requested,
    bloodbankId: payload && payload.bloodbank,
    scopeKey: donorsScopeKey(payload),
  }));
};

// Candidate count of the message editor / the live count of the filter
// modal: the smallest page (1 × 20) of the same query, only `paging.total`
// is used. Its own pending key, so it never aborts the list's page fetch.
export const getCandidateCount = (payload) =>
  guardedBatchGet('getCandidateCount', donorsBatchGetBody(payload, { page: 1, pageSize: DONORS_DEFAULT_PAGE_SIZE }), (res) => {
    const paging = (res && res.paging) || {};
    const content = res && Array.isArray(res.content) ? res.content : [];
    const total = typeof paging.total === 'number' && Number.isFinite(paging.total) ? paging.total : content.length;
    return setUsersAmount({ total, bloodbankId: payload && payload.bloodbank });
  });

// Resolves with the outcome of THIS lookup, so a caller can tie the donor to
// the PNR it asked for instead of reading the shared `userByPnr` slot (which
// keeps the previous donor until another lookup lands, and is written by
// every screen that looks a PNR up):
//   the donor ({ id, ... })  — found
//   null                     — no donor with that PNR
//   undefined                — the lookup itself failed (requestFail dispatched)
const lookedUpUser = (res) => (res && res.data ? res.data : null);

export const getUserByPnr = (userPnr) => async (dispatch) => {
  dispatch(requestPending());
  try {
    try {
      const token = await dispatch(getValidIdToken());
      const res = await getUnEncryptedUserByPnr(userPnr, token);
      dispatch(userByPnr(res));
      return lookedUpUser(res);
    } catch (err) {
      if (/Invalid token provided.*/i.test(err.message)) {
        const token = await dispatch(getIdToken());
        const res = await getUnEncryptedUserByPnr(userPnr, token);
        dispatch(userByPnr(res));
        return lookedUpUser(res);
      } else if (/no user found with pnr.*/i.test(err.message)) {
        return null;
      } else {
        throw err;
      }
    }
  } catch (err) {
    dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    dispatch(requestFail(err));
    return undefined;
  }
}

export const getDataForDonorDetails = (userId, details) => async (dispatch) => {
  dispatch(requestPending());
  try {
    try {
      const token = await dispatch(getValidIdToken());
      const res = await getUnEncryptedUserById(userId, token);
      const { donationSite } = await getUser(`/v1/donors/${userId}?view=raw`);
      dispatch(getDonorDetails({ res: { data: { ...res.data, donationSite } }, details }));
    } catch (err) {
      if (/Invalid token provided.*/i.test(err.message)) {
        const token = await dispatch(getIdToken());
        const res = await getUnEncryptedUserById(userId, token);
        const { donationSite } = await getUser(`/v1/donors/${userId}?view=raw`)
        dispatch(getDonorDetails({ res: { data: { ...res.data, donationSite } }, details }));
      } else {
        throw err;
      }
    }
  } catch (err) {
    dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    dispatch(requestFail(err));
  }
}

export const getDataForDonorSettings = (userId, reservation) => async (dispatch) => {
  dispatch(requestPending());
  try {
    try {
      const token = await dispatch(getValidIdToken());
      const res = await getUnEncryptedUserById(userId, token);
      dispatch(getDonorSettings({res, reservation}));
    } catch (err) {
      if (/Invalid token provided.*/i.test(err.message)) {
        const token = await dispatch(getIdToken());
        const res = await getUnEncryptedUserById(userId, token);
        dispatch(getDonorSettings({res, reservation}));
      } else {
        throw err;
      }
    }
  } catch (err) {
    dispatch(sendErrorLog(err.message, err.status, 'ekm', err.url));
    dispatch(requestFail(err));
  }
}